import React, { ReactNode } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import Text from '../Text';

import styles from './tooltip.module.scss';

export interface TooltipProps {
  /**
   * The content displayed inside the tooltip when it is open.
   */
  content: string;

  /**
   * The element that triggers the tooltip.
   */
  children: ReactNode;

  /**
   * The preferred placement of the tooltip relative to the target element.
   * @default 'top'
   */
  placement?: TooltipPrimitive.TooltipContentProps['side'];

  maxWidth?: string;

  /**
   * Event handler called when the open state of the tooltip changes.
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * Whether to skip wrapping the tooltip in a Provider.
   * Use this when you wish to wrap with your own TooltipProvider
   * to set custom durations between a related group of tooltips
   * @default false
   */
  skipProvider?: boolean;
}

export const TooltipProvider = ({ children }: { children: ReactNode }) => {
  return (
    <TooltipPrimitive.Provider delayDuration={200} skipDelayDuration={700}>
      {children}
    </TooltipPrimitive.Provider>
  );
};

const Tooltip = ({
  children,
  placement = 'top',
  content,
  maxWidth,
  onOpenChange,
  skipProvider = false,
}: TooltipProps) => {
  const tooltipContent = (
    <TooltipPrimitive.Root onOpenChange={onOpenChange}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          className={styles['popper-container']}
          sideOffset={5}
          side={placement}
          style={{ maxWidth: maxWidth || undefined }}
        >
          <Text height="standard" family="regular" scale="2">
            {content}
          </Text>
          <TooltipPrimitive.Arrow className={styles['popper-arrow']} />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );

  return skipProvider ? (
    tooltipContent
  ) : (
    <TooltipProvider>{tooltipContent}</TooltipProvider>
  );
};

export default Tooltip;
